import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CalendarCheck, Trophy, CalendarOff, ListTodo, Target, Camera, Users, TrendingUp, Download
} from 'lucide-react';

function SummaryCard({ icon: Icon, label, value, sub, color = 'text-neutral-950', iconColor = 'text-neutral-500' }) {
  return (
    <Card className="border border-neutral-200 shadow-sm rounded-md">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className={`h-4 w-4 ${iconColor}`} />
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
        </div>
        <p className={`text-xl font-heading font-black ${color}`}>{value}</p>
        {sub && <p className="text-[10px] text-neutral-400 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function MonthlySummaryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [filterBranch, setFilterBranch] = useState('all');
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { month };
        if (filterBranch && filterBranch !== 'all') params.branch_id = filterBranch;
        const [sRes, bRes] = await Promise.all([
          api.get('/monthly-summary', { params }),
          api.get('/branches')
        ]);
        setData(sRes.data);
        setBranches(bRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [month, filterBranch]);

  const handleExport = (type) => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    let url = `${base}/api/export/${type}?month=${month}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${type}_${month}.csv`; a.click(); });
  };

  // Generate previous 12 months for selector
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push(d.toISOString().slice(0, 7));
  }

  if (loading) return (
    <div className="space-y-6">
      <div className="h-8 w-48 bg-neutral-200 rounded animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-neutral-200 rounded animate-pulse" />)}</div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="monthly-summary-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Monthly Summary</h1>
          <p className="text-neutral-500 text-sm mt-1">{month} &middot; {data?.working_days} working days</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="summary-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          {user?.system_role !== 'branch_manager' && (
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="summary-branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard icon={Users} label="Total Staff" value={data.total_employees} iconColor="text-blue-500" />
            <SummaryCard icon={CalendarCheck} label="Attendance Rate" value={`${data.attendance_rate}%`}
              sub={`${data.total_attendance} total markings`} color="text-green-600" iconColor="text-green-500" />
            <SummaryCard icon={Trophy} label="Achievements" value={`${data.achievements_approved}/${data.achievements_submitted}`}
              sub="approved / submitted" color="text-amber-600" iconColor="text-amber-500" />
            <SummaryCard icon={ListTodo} label="Tasks Done" value={`${data.tasks_completed}/${data.tasks_total}`}
              color="text-blue-600" iconColor="text-blue-500" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard icon={Target} label="Konattu Target" value={data.konattu_target > 0 ? data.konattu_target.toLocaleString() : 'Not Set'}
              sub={data.konattu_target > 0 ? `${data.konattu_pct}% achieved` : ''} color="text-red-600" iconColor="text-red-500" />
            <SummaryCard icon={Target} label="Konattu Achieved" value={data.konattu_achieved?.toLocaleString() || '0'}
              color="text-emerald-600" iconColor="text-emerald-500" />
            <SummaryCard icon={CalendarOff} label="Leaves Approved" value={data.leaves_approved}
              color="text-purple-600" iconColor="text-purple-500" />
            <SummaryCard icon={Camera} label="Meeting Photos" value={data.meeting_photos_uploaded}
              sub="uploaded this month" iconColor="text-neutral-500" />
          </div>

          {/* Konattu Target Progress Bar */}
          {data.konattu_target > 0 && (
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                    <Target className="h-4 w-4 text-red-600" /> Konattu Sale Target Progress
                  </p>
                  <Badge className={`text-xs ${data.konattu_pct >= 100 ? 'bg-emerald-100 text-emerald-700' : data.konattu_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                    {data.konattu_pct}%
                  </Badge>
                </div>
                <div className="w-full h-4 bg-neutral-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${data.konattu_pct >= 100 ? 'bg-emerald-500' : data.konattu_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(data.konattu_pct, 100)}%` }} />
                </div>
                <div className="flex justify-between mt-2 text-xs text-neutral-500">
                  <span>Achieved: {data.konattu_achieved?.toLocaleString()}</span>
                  <span>Target: {data.konattu_target?.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Section */}
          <Card className="border border-neutral-200 shadow-sm rounded-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
                <Download className="h-4 w-4 text-green-600" /> Export Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => handleExport('attendance')} data-testid="export-att-btn">
                  <CalendarCheck className="h-3.5 w-3.5 mr-1.5 text-green-600" /> Attendance
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => handleExport('employees')} data-testid="export-emp-btn">
                  <Users className="h-3.5 w-3.5 mr-1.5 text-blue-600" /> Employees
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => handleExport('achievements')} data-testid="export-ach-btn">
                  <Trophy className="h-3.5 w-3.5 mr-1.5 text-amber-600" /> Achievements
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button variant="outline" className="h-11 rounded-sm justify-start" onClick={() => navigate('/analytics')}>
              <TrendingUp className="h-4 w-4 mr-2 text-blue-600" /> Analytics
            </Button>
            <Button variant="outline" className="h-11 rounded-sm justify-start" onClick={() => navigate('/compliance')}>
              <Target className="h-4 w-4 mr-2 text-red-600" /> Compliance
            </Button>
            <Button variant="outline" className="h-11 rounded-sm justify-start" onClick={() => navigate('/konattu-targets')}>
              <Target className="h-4 w-4 mr-2 text-amber-600" /> Konattu Targets
            </Button>
            <Button variant="outline" className="h-11 rounded-sm justify-start" onClick={() => navigate('/meeting-photos')}>
              <Camera className="h-4 w-4 mr-2 text-neutral-600" /> Meeting Photos
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
